import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { createQueryClient } from '@/api/queryClient';
import { App } from '@/App';
import { AuthProvider } from '@/auth/AuthProvider';
import { AppI18nProvider } from '@/i18n/I18nProvider';
import '@/styles/index.css';

const container = document.getElementById('root');
if (!container) {
  throw new Error('#root is missing from index.html');
}

/**
 * D9: an unset `VITE_API_BASE_URL` means "run against mocks". Both halves of the condition
 * are static in a production build, so the branch — and `mocks/` with it — is eliminated
 * rather than shipped and skipped.
 *
 * Awaited before the first render: a query that fires while the service worker is still
 * registering would go to the network and 404 against the dev server.
 */
async function bootMocks(): Promise<void> {
  if (!import.meta.env.DEV || import.meta.env.VITE_API_BASE_URL) return;
  const { startMocks } = await import('../mocks/browser');
  await startMocks();
}

// Four providers, in this order: the cache outlives sign-in state, auth must wrap the
// router because the guard reads it, i18n needs both (the profile carries the locale, and
// fetching a non-`en` bundle needs a token) and must sit above the router so the language
// does not reset between routes, and the router must wrap the app.
void bootMocks().then(() => {
  createRoot(container).render(
    <StrictMode>
      <QueryClientProvider client={createQueryClient()}>
        <AuthProvider>
          <AppI18nProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </AppI18nProvider>
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
});
